/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import { UapiMcpCore } from "../core.js";
import { appendForm } from "../lib/encodings.js";
import {
  getContentTypeFromFileName,
  readableStreamToArrayBuffer,
} from "../lib/files.js";
import { compactMap } from "../lib/primitives.js";
import { safeParse } from "../lib/schemas.js";
import { RequestOptions } from "../lib/sdks.js";
import { extractSecurity, resolveGlobalSecurity } from "../lib/security.js";
import { pathToFunc } from "../lib/url.js";
import { APIError } from "../models/errors/apierror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import {
  PostImageMotouRequest,
  PostImageMotouRequest$zodSchema,
} from "../models/postimagemotouop.js";
import { APICall, APIPromise } from "../types/async.js";
import { isBlobLike } from "../types/blobs.js";
import { Result } from "../types/fp.js";
import { isReadableStream } from "../types/streams.js";

export enum PostImageMotouAcceptEnum {
  applicationJsonAccept = "application/json",
  imageGifAccept = "image/gif",
}

/**
 * 生成摸摸头GIF
 *
 * @remarks
 * 除了使用QQ头像，你还可以通过上传自己的图片或提供图片URL来制作独一无二的摸摸头GIF。
 *
 * ## 功能概述
 * 此接口通过POST方法，支持两种方式生成GIF：
 * 1.  **图片URL**：在表单中提供 `image_url` 字段。
 * 2.  **上传图片**：在表单中上传 `file` 文件。
 *
 * ## 使用须知
 * - **响应格式**：接口成功时直接返回 `image/gif` 格式的二进制数据。
 * - **参数优先级**：如果同时提供了 `image_url` 和上传的 `file` 文件，系统将 **优先使用 `image_url`**。
 * - **背景颜色**：同样支持 `bg_color` 表单字段来控制GIF背景。
 */
export function imagePostImageMotou(
  client$: UapiMcpCore,
  request: PostImageMotouRequest,
  options?: RequestOptions,
): APIPromise<
  Result<
    Response,
    | APIError
    | SDKValidationError
    | UnexpectedClientError
    | InvalidRequestError
    | RequestAbortedError
    | RequestTimeoutError
    | ConnectionError
  >
> {
  return new APIPromise($do(
    client$,
    request,
    options,
  ));
}

async function $do(
  client$: UapiMcpCore,
  request: PostImageMotouRequest,
  options?: RequestOptions & {
    acceptHeaderOverride?: PostImageMotouAcceptEnum;
  },
): Promise<
  [
    Result<
      Response,
      | APIError
      | SDKValidationError
      | UnexpectedClientError
      | InvalidRequestError
      | RequestAbortedError
      | RequestTimeoutError
      | ConnectionError
    >,
    APICall,
  ]
> {
  const parsed$ = safeParse(
    request,
    (value$) => PostImageMotouRequest$zodSchema.parse(value$),
    "Input validation failed",
  );
  if (!parsed$.ok) {
    return [parsed$, { status: "invalid" }];
  }
  const payload$ = parsed$.value;
  const body$ = new FormData();
  if (payload$.bg_color !== undefined) {
    appendForm(body$, "bg_color", payload$.bg_color);
  }
  if (payload$.file !== undefined) {
    if (isBlobLike(payload$.file)) {
      appendForm(body$, "file", payload$.file);
    } else if (isReadableStream(payload$.file.content)) {
      const buffer = await readableStreamToArrayBuffer(payload$.file.content);
      const contentType = getContentTypeFromFileName(payload$.file.fileName)
        || "application/octet-stream";
      const blob = new Blob([buffer], { type: contentType });
      appendForm(body$, "file", blob, payload$.file.fileName);
    } else {
      const contentType = getContentTypeFromFileName(payload$.file.fileName)
        || "application/octet-stream";
      appendForm(
        body$,
        "file",
        new Blob([payload$.file.content], { type: contentType }),
        payload$.file.fileName,
      );
    }
  }
  if (payload$.image_url !== undefined) {
    appendForm(body$, "image_url", payload$.image_url);
  }
  const path$ = pathToFunc("/image/motou")();

  const headers$ = new Headers(compactMap({
    Accept: options?.acceptHeaderOverride
      || "application/json;q=1, image/gif;q=0",
  }));
  const securityInput = await extractSecurity(client$._options.security);
  const requestSecurity = resolveGlobalSecurity(securityInput);

  const context = {
    options: client$._options,
    baseURL: options?.serverURL ?? client$._baseURL ?? "",
    operationID: "post-image-motou",
    oAuth2Scopes: null,
    resolvedSecurity: requestSecurity,
    securitySource: client$._options.security,
    retryConfig: options?.retries
      || client$._options.retryConfig
      || { strategy: "none" },
    retryCodes: options?.retryCodes || [
      "429",
      "500",
      "502",
      "503",
      "504",
    ],
  };

  const requestRes = client$._createRequest(context, {
    security: requestSecurity,
    method: "POST",
    baseURL: options?.serverURL,
    path: path$,
    headers: headers$,
    body: body$,
    userAgent: client$._options.userAgent,
    timeoutMs: options?.timeoutMs || client$._options.timeoutMs
      || -1,
  }, options);
  if (!requestRes.ok) {
    return [requestRes, { status: "invalid" }];
  }
  const req$ = requestRes.value;

  const doResult = await client$._do(req$, {
    context,
    errorCodes: [],
    retryConfig: context.retryConfig,
    retryCodes: context.retryCodes,
  });
  if (!doResult.ok) {
    return [doResult, { status: "request-error", request: req$ }];
  }
  return [doResult, {
    status: "complete",
    "request": req$,
    response: doResult.value,
  }];
}
